import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Music, Filter, Users, X } from "lucide-react";
import type { Performer, JamNight } from "@shared/schema";
import StageSelectionModal from "@/components/stage-selection-modal";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

// Import new modular components
import { getModeConfig, type ParticipantMode } from "./participants/mode-config";
import { ParticipantFilters } from "./participants/participant-filters";
import { useParticipantMutations } from "./participants/use-participant-mutations";
import { ParticipantDesktopRow } from "./participants/participant-desktop-row";
import { ParticipantMobileCard } from "./participants/participant-mobile-card";
import { usePersistedState } from "@/hooks/use-persisted-state";

interface UnifiedParticipantsTableProps {
  performers: Performer[];
  jamNightId?: number;
  jamNight?: JamNight;
  disabled?: boolean;
  mode: ParticipantMode;
  forceMobileView?: boolean;
}


export default function UnifiedParticipantsTable({
  performers,
  jamNightId,
  jamNight,
  disabled = false,
  mode,
  forceMobileView = false,
}: UnifiedParticipantsTableProps) {
  // Create unique keys for each filter based on jamNightId to separate different events
  const filterKeyPrefix = jamNightId ? `jam-${jamNightId}` : 'global';
  
  // Use persisted state for filters
  const [searchTerm, setSearchTerm] = usePersistedState(`${filterKeyPrefix}-search`, "");
  const [playedFilter, setPlayedFilter] = usePersistedState(`${filterKeyPrefix}-played`, "all");
  const [attendedFilter, setAttendedFilter] = usePersistedState<"all" | "attended" | "not-attended">(`${filterKeyPrefix}-attended`, "not-attended");
  const [showPriorityOnly, setShowPriorityOnly] = usePersistedState(`${filterKeyPrefix}-priority`, false);
  const [instrumentFilter, setInstrumentFilter] = usePersistedState<string[]>(`${filterKeyPrefix}-instruments`, []);
  const [typeFilter, setTypeFilter] = usePersistedState<string[]>(`${filterKeyPrefix}-types`, []);
  
  // Non-persisted state
  const [showFilters, setShowFilters] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedPerformer, setSelectedPerformer] = useState<Performer | null>(null);
  const [sortedPerformers, setSortedPerformers] = useState<Performer[]>([]);

  // Get mode configuration - memoized to prevent infinite re-renders
  const config = useMemo(() => getModeConfig(mode, performers), [mode, performers]);

  // Memoize the onStageModal object to prevent infinite re-renders
  const onStageModal = useMemo(() => ({
    setSelectedPerformer,
    setShowStageModal,
  }), []);

  // Use the modular mutations hook
  const {
    updatePerformerMutation,
    handleActionToggle,
    handleStageSelection,
    handleRemovePerformer,
    handleConsider,
    isLoading,
  } = useParticipantMutations({
    jamNightId,
    mode,
    performers,
    onStageModal,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 50,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Filter available performers based on mode - use primitive dependencies
  const availablePerformers = useMemo(() => {
    const filtered = performers.filter((performer) => {
      if (performer.removed) return false;
      
      // All modes should only show performers with 'participant' status
      // This ensures that performers in consideration, selected, on_stage, or pending_removal
      // are not shown in the main participants list
      return performer.status === 'participant';
    });
    console.log('[UnifiedParticipantsTable] Available performers:', filtered.length, 'out of', performers.length, 'total');
    console.log('[UnifiedParticipantsTable] Performer statuses:', performers.map(p => ({ name: p.name, status: p.status })));
    return filtered;
  }, [performers, mode]);

  useEffect(() => {
    const sorted = [...availablePerformers].sort((a, b) => {
      const soA = a.sortOrder ?? Infinity;
      const soB = b.sortOrder ?? Infinity;
      if (soA === soB) {
        return (a.id ?? 0) - (b.id ?? 0);
      }
      return soA - soB;
    });
    setSortedPerformers(sorted);
  }, [availablePerformers]);

  // Default filter for played/attended based on mode - use primitive dependencies
  useEffect(() => {
    if (mode === 'generic-event') {
      setAttendedFilter("not-attended");
      setPlayedFilter("all");
    } else if (mode === 'open-mic') {
      setPlayedFilter("not-played");
      setAttendedFilter("all");
    } else {
      // Jam Night defaults to "all"
      setPlayedFilter("all");
      setAttendedFilter("all");
    }
  }, [mode]);


  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedPerformers.findIndex((p) => p.id === active.id);
      const newIndex = sortedPerformers.findIndex((p) => p.id === over.id);

      const newOrder = arrayMove(sortedPerformers, oldIndex, newIndex);
      setSortedPerformers(newOrder);

      newOrder.forEach((performer, index) => {
        const newSortOrder = (index + 1) * 1000;
        if (performer.sortOrder !== newSortOrder) {
          updatePerformerMutation.mutate({
            id: performer.id,
            updates: { sortOrder: newSortOrder },
          });
        }
      });
    }
  };

  // Filter performers based on selected filters - use primitive dependencies
  const filteredPerformers = useMemo(() => {
    return sortedPerformers.filter((performer) => {
      // Search filter
      if (
        searchTerm &&
        !performer.name.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }

      // Status filter
      if (mode === 'generic-event') {
        if (attendedFilter === "attended" && !performer.attended) return false;
        if (attendedFilter === "not-attended" && performer.attended) return false;
      } else {
        if (playedFilter === "played" && !performer.played) return false;
        if (playedFilter === "not-played" && performer.played) return false;
      }

      // Priority filter
      if (showPriorityOnly && !performer.priority) return false;

      // Instrument filter (Jam Night only)
      if (mode === 'jam-night' && instrumentFilter.length > 0) {
        const hasMatchingInstrument = performer.instruments?.some((instrument) =>
          instrumentFilter.includes(instrument),
        );
        if (!hasMatchingInstrument) return false;
      }

      // Type filter (Open Mic only)
      if (mode === 'open-mic' && typeFilter.length > 0) {
        if (!typeFilter.includes(performer.performerType || "solo")) return false;
      }

      return true;
    });
  }, [sortedPerformers, searchTerm, playedFilter, attendedFilter, showPriorityOnly, instrumentFilter, typeFilter, mode]);

  // Generate table headers based on mode - memoized to prevent re-renders
  const tableHeaders = useMemo(() => {
    const headers = ["Name"];
    
    if (mode === 'jam-night') {
      headers.push("Instruments", "Tier");
    } else if (mode === 'open-mic') {
      headers.push("Type");
    }
    
    headers.push("Priority");
    headers.push(mode === 'generic-event' ? "Attended" : "Played");
    
    if (mode === 'jam-night') {
      headers.push("Times Performed");
    }
    
    if (mode !== 'generic-event') {
      headers.push("Time on Stage");
    }
    
    headers.push("Actions");
    return headers;
  }, [mode]);

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== "" || 
    playedFilter !== "all" || 
    attendedFilter !== "all" || 
    showPriorityOnly || 
    instrumentFilter.length > 0 || 
    typeFilter.length > 0;

  // Clear all filters function
  const clearAllFilters = () => {
    setSearchTerm("");
    setPlayedFilter("all");
    setAttendedFilter("all");
    setShowPriorityOnly(false);
    setInstrumentFilter([]);
    setTypeFilter([]);
  };

  return (
    <div className="bg-white/30 rounded-2xl shadow-lg border border-white/30 overflow-hidden relative">
      
      <div className="px-6 py-4 border-b border-white/20 relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900/90 flex items-center drop-shadow-sm">
              {config.isGenericEvent ? <Users className="h-5 w-5 mr-2 text-blue-600 drop-shadow-sm" /> : <Music className="h-5 w-5 mr-2 text-blue-600 drop-shadow-sm" />}
              {config.isOpenMic ? "Participants" : "Participants"}
              <span className="ml-2 text-sm font-normal text-slate-600">
                ({filteredPerformers.length})
              </span>
            </h2>
          </div>
          {hasActiveFilters && (
            <Button
              size="sm"
              variant="outline"
              onClick={clearAllFilters}
              className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300 h-7 px-3 text-xs"
              style={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              <X className="h-3 w-3 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <ParticipantFilters
        config={config}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        playedFilter={playedFilter}
        onPlayedFilterChange={setPlayedFilter}
        attendedFilter={attendedFilter}
        onAttendedFilterChange={setAttendedFilter}
        showPriorityOnly={showPriorityOnly}
        onPriorityFilterChange={setShowPriorityOnly}
        instrumentFilter={instrumentFilter}
        onInstrumentFilterChange={setInstrumentFilter}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        showFilters={showFilters}
        onShowFiltersChange={setShowFilters}
        onClearAllFilters={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
        forceMobileView={forceMobileView}
      />

      {/* Mobile Table */}
      <div className={forceMobileView ? "block" : "block lg:hidden"}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={disabled ? undefined : handleDragEnd}
        >
          <SortableContext
            items={filteredPerformers.map((p) => p.id)}
            strategy={verticalListSortingStrategy}
          >
            {filteredPerformers.map((performer, index) => (
              <ParticipantMobileCard
                key={performer.id}
                performer={performer}
                mode={mode}
                config={config}
                onActionToggle={handleActionToggle}
                onConsider={handleConsider}
                onRemove={handleRemovePerformer}
                isLoading={isLoading}
                isFirst={index === 0}
                disabled={disabled}
                jamNightId={jamNightId}
                jamNight={jamNight}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {/* Desktop Table */}
      <div className={forceMobileView ? "hidden" : "hidden lg:block overflow-x-auto"}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={disabled ? undefined : handleDragEnd}
        >
          <table className="min-w-full divide-y divide-white/20">
            <thead className={config.isJamNight ? "bg-white/20" : "bg-slate-50/40"}>
              <tr>
                {tableHeaders.map((header) => (
                  <th
                    key={header}
                    className="px-6 py-3 text-left text-xs font-medium text-slate-700/90 uppercase tracking-wider drop-shadow-sm"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={config.isJamNight ? "bg-white/10 divide-y divide-white/20" : "bg-white/30 divide-y divide-slate-200/30"}>
              <SortableContext
                items={filteredPerformers.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredPerformers.map((performer, index) => (
                  <ParticipantDesktopRow
                    key={performer.id}
                    performer={performer}
                    mode={mode}
                    config={config}
                    onActionToggle={handleActionToggle}
                    onConsider={handleConsider}
                    onRemove={handleRemovePerformer}
                    isLoading={isLoading}
                    isFirst={index === 0}
                    disabled={disabled}
                    jamNightId={jamNightId}
                    jamNight={jamNight}
                  />
                ))}
              </SortableContext>
            </tbody>
          </table>
        </DndContext>
      </div>

      {performers.length === 0 ? (
        <div className="p-8 text-center text-slate-500">
          {config.isGenericEvent ? <Users className="h-8 w-8 mx-auto mb-4 opacity-50" /> : <Music className="h-8 w-8 mx-auto mb-4 opacity-50" />}
          <p>No participants yet. Add someone to get started!</p>
        </div>
      ) : filteredPerformers.length === 0 ? (
        <div className="p-8 text-center text-slate-500">
          <Filter className="h-8 w-8 mx-auto mb-4 opacity-50" />
          <p>No participants match the selected filters.</p>
          <Button
            variant="outline"
            onClick={() => {
              setSearchTerm("");
              if (config.isGenericEvent) {
                setAttendedFilter("not-attended");
              } else {
                setPlayedFilter("all");
              }
              setInstrumentFilter([]);
              setTypeFilter([]);
              setShowPriorityOnly(false);
            }}
            className="mt-2"
          >
            Clear Filters
          </Button>
        </div>
      ) : null}

      {/* Stage Selection Modal (Jam Night only) */}
      {config.isJamNight && (
        <StageSelectionModal
          open={showStageModal}
          onOpenChange={setShowStageModal}
          performerName={selectedPerformer?.name || ""}
          availableInstruments={selectedPerformer?.instruments || []}
          onStagePerformers={performers.filter((p) => p.status === 'on_stage')}
          onConfirm={(selectedInstruments, replacementPerformerId) => 
            selectedPerformer && handleStageSelection(selectedPerformer, selectedInstruments, replacementPerformerId)
          }
        />
      )}
      
    </div>
  );
}